import { SortingState } from "@tanstack/react-table";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportGRNCSV,
  fetchGRNList,
} from "slices/Warehouse/ItemInward/GRN/thunk";

import { Visibility } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { exportPendingPOCSV } from "slices/Warehouse/ItemInward/PendingPO/thunk";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import {
  fetchDailyLogs,
  exportDailyLogCSV,
} from "slices/Warehouse/ProjectWarehouse/DailyLog/thunk";
import { clearDailyLogs } from "slices/Warehouse/ProjectWarehouse/DailyLog/reducer";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";

type DailyLogProps = {
  warehouseId: string ;
};

const WarehouseDailyLog: React.FC<DailyLogProps> = ({ warehouseId }) => {
  document.title = "Warehouse Daily Log";

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.STOCK_OVERVIEW.DAILY_LOG,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const Warehouse = createSelector(
    (state: any) => state.DailyLog,
    (item) => ({
      list: Array.isArray(item?.list) ? item.list : item?.list?.data || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading ?? false,
      error: item?.error ?? null,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(Warehouse);

  useEffect(() => {
    if (warehouseId) {
      dispatch(
        fetchDailyLogs({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortDirection,
          search,
          filters: filterValue,
          warehouse_id: warehouseId,
        }),
      );
    } else {
      dispatch(clearDailyLogs());
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseId,
  ]);

  useEffect(() => {
    if (list && list.length > 0) {
      setData(list);
    } else {
      setData([]);
    }
  }, [list]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const canView = true;

  const columns = useMemo(() => {
    return [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Product Name",
        accessorKey: "item_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "item_name",
            warehouseId,
          ),
      },
      {
        header: "Product SKU",
        accessorKey: "product.product_sku",
        id: "item_sku",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "item_sku",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original.product?.product_sku || "-",
      },
      {
        header: "Make",
        accessorKey: "product.make",
        id: "make",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "make",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.make || "-",
      },
      {
        header: "Model",
        accessorKey: "product.model",
        id: "model",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "model",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.model || "-",
      },
      {
        header: "Unit",
        accessorKey: "product.unit",
        id: "unit",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "unit",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.unit || "-",
      },
      {
        header: "Opening Stock",
        accessorKey: "opening_stock",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "opening_stock",
            warehouseId,
          ),
      },
      {
        header: "Today In ",
        accessorKey: "today_in",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "today_in",
            warehouseId,
          ),
      },
      {
        header: "Today Out",
        accessorKey: "today_out",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "today_out",
            warehouseId,
          ),
      },
      {
        header: "Closing Stock",
        accessorKey: "closing_stock",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "closing_stock",
            warehouseId,
          ),
      },
      {
        header: "Category",
        accessorKey: "product.category",
        id: "category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.product?.category || "-",
      },
      {
        header: "Sub Category",
        accessorKey: "product.sub_category",
        id: "sub_category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.STOCK_OVERVIEW?.DAILY_LOG?.FILTER_DROPDOWN(
            "sub_category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original.product?.sub_category || "-",
      },
    ];
  }, [dispatch, list, navigate, canView, warehouseId]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            <ExportButton
              onExport={async () => {
                return await exportDailyLogCSV({
                  warehouse_id: warehouseId,
                  search,
                  filters: filterValue,
                });
              }}
            />
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <MasterTableContainer
            columns={columns}
            data={data || []}
            totalRecords={
              Number.isNaN(rowCount) || !rowCount ? 0 : Number(rowCount)
            }
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>
    </div>
  );
};

export default WarehouseDailyLog;
